// Inspired by https://www.shadcn.io/components/media/tags

import { cn } from '@repo/ui/utils'
import { X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Select, SelectContent, SelectItem, SelectTrigger } from './select'

export type TagItem = {
  id: string
  label: string
}

const Tag = ({ item, onRemove }: { item: TagItem; onRemove: () => void }) => {
  return (
    <div className="bg-primary-100 text-primary-900 rounded-md px-2 py-1 text-xs inline-flex items-center gap-2 relative z-10">
      <span className="text-primary-900">{item.label}</span>
      <button type="button" className="text-primary-900 hover:text-primary-600" onClick={onRemove}>
        <X className="size-3" />
      </button>
    </div>
  )
}

export function Tags({
  className,
  initialSelectedIds = [],
  items,
  onChange,
  placeholder,
}: {
  className?: string
  initialSelectedIds?: string[]
  items: TagItem[]
  onChange: (selectedItemIds: string[]) => void
  canUnselectAll?: boolean
  placeholder?: string
}) {
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>(initialSelectedIds)

  const selectedItems = items.filter((item) => selectedItemIds.includes(item.id))
  const selectableItems = items.filter((item) => !selectedItemIds.includes(item.id))

  const handleRemove = (id: string) => {
    setSelectedItemIds(selectedItemIds.filter((itemId) => itemId !== id))
  }

  useEffect(() => {
    onChange(selectedItemIds)
  }, [selectedItemIds])

  const handleSelect = (id: string) => {
    if (id === 'no-items') return

    setSelectedItemIds([...selectedItemIds, id])
  }

  return (
    <div
      className={cn(
        'border-primary-200 text-primary ring-offset-primary-600 focus-visible:ring-primary-600 focus-visible:ring-accent w-full rounded-md border bg-white px-2 py-1 flex items-center text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 relative',
        className,
      )}
    >
      <div className="flex flex-wrap gap-2 items-center min-h-[1.375rem]">
        {selectedItems.map((item) => (
          <Tag key={item.id} item={item} onRemove={() => handleRemove(item.id)} />
        ))}
        <Select onValueChange={(value) => handleSelect(value)} value={''}>
          <SelectTrigger className="absolute left-0 top-0 w-full h-full opacity-0"></SelectTrigger>
          <SelectContent position="popper">
            {selectableItems.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.label}
              </SelectItem>
            ))}
            {selectableItems.length === 0 && <SelectItem value="no-items">No items</SelectItem>}
          </SelectContent>
        </Select>
        <span className="text-primary-500">{placeholder}</span>
      </div>
    </div>
  )
}
